import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useCurriculum } from "@/context/CurriculumContext";
import { toast } from "sonner";
import { Modal, Field } from "@/pages/Dashboard";
import { ScoreRing, BandPill, LevelChip } from "@/components/Bands";
import { SUBJECT_META, SUBJECT_META_ALL, levelStyle, overallBand, TARGET_STATUS } from "@/lib/scale";
import { TOOLTIP_STYLE_NUNITO, TOOLTIP_LABEL, TICK_MICRO, DOMAIN_0_45, INIT_DIM_SM } from "@/lib/chartStyles";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import ChartFrame from "@/components/ChartFrame";
import {
  ChevronLeft, Loader2, ClipboardEdit, Target, Plus, Printer, Trash2, Check, CircleDot, Circle, Sparkles, FileText, ClipboardCheck, Activity, ListChecks, NotebookPen, LayoutDashboard, Share2, FileBarChart2, Users2, Star, Layers,
} from "lucide-react";
import TestsSection from "@/components/student/TestsSection";
import RunningSection from "@/components/student/RunningSection";
import ObservationsSection from "@/components/student/ObservationsSection";
import SelfEvalSection from "@/components/student/SelfEvalSection";
import SelfEvalCard from "@/components/student/SelfEvalCard";
import HolisticSummaryModal from "@/components/student/HolisticSummaryModal";
import ParentShareModal from "@/components/ParentShareModal";

const TABS = [
  { key: "overview", label: "Overview", Icon: LayoutDashboard },
  { key: "tests", label: "Tests & Scores", Icon: Activity },
  { key: "running", label: "Running Assessments", Icon: ListChecks },
  { key: "observations", label: "Observations", Icon: NotebookPen },
  { key: "selfeval", label: "Get to Know Me", Icon: Star },
];

export default function StudentProfile() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const curriculum = useCurriculum();
  const [student, setStudent] = useState(null);
  const [targetModal, setTargetModal] = useState(false);
  const [aiModal, setAiModal] = useState(false);
  const [summaryModal, setSummaryModal] = useState(false);
  const [parentShare, setParentShare] = useState(false);
  const [groups, setGroups] = useState([]);
  const [tab, setTab] = useState("overview");

  const load = useCallback(() => {
    api.get(`/students/${studentId}`).then((r) => setStudent(r.data)).catch(() => navigate("/"));
    api.get(`/students/${studentId}/groups`).then((r) => setGroups(r.data || [])).catch(() => {});
  }, [studentId, navigate]);
  useEffect(() => { load(); }, [load]);

  const subjects = useMemo(() => {
    const all = curriculum?.subjects || {};
    const isAsd = student?.is_asd;
    return Object.fromEntries(
      Object.entries(all).filter(([, s]) => !s.optional || isAsd)
    );
  }, [curriculum, student]);

  if (!student || !curriculum)
    return <div className="flex justify-center py-24 text-slate-300"><Loader2 className="animate-spin" /></div>;

  return (
    <div>
      <button
        data-testid="back-to-class"
        onClick={() => navigate(`/class/${student.class_id}`)}
        className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 transition mb-5"
      >
        <ChevronLeft size={16} /> {student.class_name || "Class"}
      </button>

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div className="flex items-center gap-4">
          <span className="h-16 w-16 rounded-3xl bg-[#4A5D6B] text-white flex items-center justify-center font-['Outfit'] font-bold text-2xl">
            {student.name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase()}
          </span>
          <div>
            <h1 className="font-['Outfit'] text-4xl font-bold text-slate-800 tracking-tight">{student.name}</h1>
            <p className="text-slate-500 mt-0.5">{student.class_level} · {student.class_name}</p>
            {student.is_asd && (
              <span data-testid="asd-badge" className="inline-flex items-center gap-1 mt-2 text-xs font-semibold rounded-full px-2.5 py-1 bg-[#3E9B96]/12 text-[#2c7a76]">
                <Star size={12} /> ASD profile
              </span>
            )}
            {groups.length > 0 && (
              <div className="flex items-center gap-1.5 mt-2 flex-wrap" data-testid="student-groups">
                {groups.map((g) => (
                  <button key={g.id} data-testid={`student-group-${g.id}`} onClick={() => navigate(`/group/${g.id}`)}
                    className="inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-1 bg-[#9A7AA0]/12 text-[#7a5e80] hover:bg-[#9A7AA0]/22 transition">
                    <Users2 size={12} /> {g.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            data-testid="open-cos-plan-btn"
            onClick={() => navigate(`/student/${studentId}/cos`)}
            className="text-white rounded-full px-5 py-2.5 font-semibold font-['Outfit'] transition flex items-center gap-2 shadow-sm hover:shadow-md"
            style={{ background: "linear-gradient(135deg,#7FB069,#5d8a5a)" }}
          >
            <Layers size={17} /> CoS Plan
          </button>
          <button
            data-testid="open-support-plan-btn"
            onClick={() => navigate(`/student/${studentId}/support-plan`)}
            className="bg-white border border-slate-200 hover:border-[#7FB069] text-slate-700 rounded-full px-5 py-2.5 font-semibold font-['Outfit'] transition flex items-center gap-2"
          >
            <FileText size={17} /> Support Plan
          </button>
          <button
            data-testid="open-support-review-btn"
            onClick={() => navigate(`/student/${studentId}/support-review`)}
            className="bg-white border border-slate-200 hover:border-[#7FB069] text-slate-700 rounded-full px-5 py-2.5 font-semibold font-['Outfit'] transition flex items-center gap-2"
          >
            <ClipboardCheck size={17} /> Support Review
          </button>
          <button
            data-testid="open-targets-sheet-btn"
            onClick={() => navigate(`/student/${studentId}/targets-sheet`)}
            className="bg-white border border-slate-200 hover:border-[#F6C358] text-slate-700 rounded-full px-5 py-2.5 font-semibold font-['Outfit'] transition flex items-center gap-2"
          >
            <Target size={17} /> Targets Sheet
          </button>
          <button
            data-testid="open-summary-btn"
            onClick={() => setSummaryModal(true)}
            className="text-white rounded-full px-5 py-2.5 font-semibold font-['Outfit'] transition flex items-center gap-2"
            style={{ background: "linear-gradient(135deg,#81B29A,#5d8a73)" }}
          >
            <Sparkles size={17} /> AI Summary
          </button>
          <button
            data-testid="open-parent-report-btn"
            onClick={() => navigate(`/student/${studentId}/parent-report`)}
            className="text-white rounded-full px-5 py-2.5 font-semibold font-['Outfit'] transition flex items-center gap-2"
            style={{ background: "linear-gradient(135deg,#7FB069,#5d8a5a)" }}
          >
            <FileBarChart2 size={17} /> Parent Report
          </button>
          <button
            data-testid="open-report-btn"
            onClick={() => navigate(`/student/${studentId}/report`)}
            className="bg-white border border-slate-200 hover:border-[#81B29A] text-slate-700 rounded-full px-5 py-2.5 font-semibold font-['Outfit'] transition flex items-center gap-2"
          >
            <Printer size={17} /> Report
          </button>
          <button
            data-testid="share-with-parents-btn"
            onClick={() => setParentShare(true)}
            className="bg-white border border-slate-200 hover:border-[#7FB069] text-slate-700 rounded-full px-5 py-2.5 font-semibold font-['Outfit'] transition flex items-center gap-2"
          >
            <Share2 size={17} /> Share with Parents
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1.5 mb-7 border-b border-slate-200 overflow-x-auto">
        {TABS.map(({ key, label, Icon }) => (
          <button
            key={key}
            data-testid={`tab-${key}`}
            onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold font-['Outfit'] border-b-2 -mb-px whitespace-nowrap transition ${
              tab === key ? "border-[#4A5D6B] text-[#4A5D6B]" : "border-transparent text-slate-400 hover:text-slate-600"
            }`}
          >
            <Icon size={16} /> {label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <>
          {/* Subject cards */}
          <div className="grid lg:grid-cols-3 gap-5 mb-8">
            {Object.keys(subjects).map((key) => (
              <SubjectCard
                key={key}
                subject={subjects[key]}
                assessments={student.assessments.filter((a) => a.subject === key)}
                onAssess={() => navigate(`/student/${studentId}/assess/${key}`)}
              />
            ))}
            <SelfEvalCard studentId={studentId} />
          </div>

          {/* Targets */}
          <TargetsSection
            student={student}
            subjects={subjects}
            onAdd={() => setTargetModal(true)}
            onAi={() => setAiModal(true)}
            hasAssessments={student.assessments.length > 0}
            reload={load}
          />
        </>
      )}

      {tab === "tests" && <TestsSection studentId={studentId} />}
      {tab === "running" && <RunningSection studentId={studentId} />}
      {tab === "observations" && <ObservationsSection studentId={studentId} subjects={subjects} />}
      {tab === "selfeval" && <SelfEvalSection studentId={studentId} />}

      {summaryModal && <HolisticSummaryModal studentId={studentId} onClose={() => setSummaryModal(false)} />}
      {parentShare && <ParentShareModal studentId={studentId} studentName={student.name} onClose={() => setParentShare(false)} />}

      {targetModal && (
        <AddTargetModal
          studentId={studentId}
          subjects={subjects}
          onClose={() => setTargetModal(false)}
          onSaved={() => { setTargetModal(false); load(); }}
        />
      )}
      {aiModal && (
        <AiTargetsModal
          studentId={studentId}
          subjects={subjects}
          onClose={() => setAiModal(false)}
          onSaved={load}
        />
      )}
    </div>
  );
}

function SubjectCard({ subject, assessments, onAssess }) {
  const latest = assessments[0];
  const meta = SUBJECT_META_ALL[subject.key];
  const dotStyle = useMemo(() => ({ r: 3, fill: meta.tint }), [meta.tint]);
  const chartData = [...assessments].reverse().map((a) => ({
    date: new Date(a.created_at).toLocaleDateString("en-IE", { day: "numeric", month: "short" }),
    total: a.total,
  }));

  return (
    <div
      data-testid={`subject-card-${subject.key}`}
      className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_24px_rgba(42,59,71,0.04)] p-6 flex flex-col"
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-wide" style={{ color: meta.tint }}>{subject.name}</p>
          {latest ? (
            <BandPill bandKey={latest.band} label={overallBand(latest.total).name} testId={`band-${subject.key}`} />
          ) : (
            <span className="text-sm text-slate-400">Not assessed</span>
          )}
        </div>
        {latest && <ScoreRing total={latest.total} />}
      </div>

      {latest ? (
        <>
          <div className="grid grid-cols-5 gap-1.5 mt-5">
            {subject.domains.map((d) => {
              const lvl = latest.scores[d.key];
              const st = levelStyle(lvl);
              return (
                <div key={d.key} className="text-center" title={`${d.name}: ${lvl}/9`}>
                  <div className="h-16 rounded-lg flex items-end overflow-hidden bg-slate-50">
                    <div className="w-full rounded-t-lg" style={{ height: `${(lvl / 9) * 100}%`, backgroundColor: st.solid }} />
                  </div>
                  <p className="text-[9px] text-slate-400 mt-1 leading-tight truncate">{d.name.split(" ")[0]}</p>
                </div>
              );
            })}
          </div>

          {chartData.length > 1 && (
            <ChartFrame className="h-20 mt-4 -ml-2">
              <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1} initialDimension={INIT_DIM_SM}>
                <LineChart data={chartData}>
                  <Tooltip
                    contentStyle={TOOLTIP_STYLE_NUNITO}
                    labelStyle={TOOLTIP_LABEL}
                  />
                  <XAxis dataKey="date" tick={TICK_MICRO} axisLine={false} tickLine={false} />
                  <YAxis domain={DOMAIN_0_45} hide />
                  <Line type="monotone" dataKey="total" stroke={meta.tint} strokeWidth={2.5} dot={dotStyle} />
                </LineChart>
              </ResponsiveContainer>
            </ChartFrame>
          )}
          <p className="text-xs text-slate-400 mt-3">
            Last assessed {new Date(latest.created_at).toLocaleDateString("en-IE", { day: "numeric", month: "short", year: "numeric" })} · {assessments.length} record{assessments.length > 1 ? "s" : ""}
          </p>
        </>
      ) : (
        <p className="text-sm text-slate-400 mt-5 flex-1">{subject.tagline}</p>
      )}

      <button
        data-testid={`assess-btn-${subject.key}`}
        onClick={onAssess}
        className="mt-5 w-full rounded-full py-2.5 font-semibold font-['Outfit'] text-white transition hover:opacity-90 flex items-center justify-center gap-2"
        style={{ backgroundColor: meta.tint }}
      >
        <ClipboardEdit size={17} /> {latest ? "New assessment" : "Assess now"}
      </button>
    </div>
  );
}

function TargetsSection({ student, subjects, onAdd, onAi, hasAssessments, reload }) {
  const targets = student.targets || [];
  const cycle = { not_started: "in_progress", in_progress: "achieved", achieved: "not_started" };
  const icons = { not_started: Circle, in_progress: CircleDot, achieved: Check };

  const setStatus = async (t) => {
    await api.patch(`/targets/${t.id}`, { status: cycle[t.status] });
    reload();
  };
  const remove = async (id) => { await api.delete(`/targets/${id}`); reload(); };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_24px_rgba(42,59,71,0.04)] p-6">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <h2 className="font-['Outfit'] text-2xl font-bold text-slate-800 flex items-center gap-2">
          <Target size={22} className="text-[#b07d3a]" /> Priority Learning Targets
        </h2>
        <div className="flex items-center gap-2">
          <button
            data-testid="ai-generate-targets-btn"
            onClick={onAi}
            disabled={!hasAssessments}
            title={hasAssessments ? "" : "Assess the student first"}
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold font-['Outfit'] text-white transition disabled:opacity-50"
            style={{ background: "linear-gradient(135deg,#81B29A,#5d8a73)" }}
          >
            <Sparkles size={16} /> Generate with AI
          </button>
          <button
            data-testid="add-target-btn"
            onClick={onAdd}
            className="bg-[#F6C358]/20 hover:bg-[#F6C358]/35 text-[#9a7016] rounded-full px-4 py-2 font-semibold font-['Outfit'] text-sm transition flex items-center gap-1.5"
          >
            <Plus size={16} /> Set target
          </button>
        </div>
      </div>

      {targets.length === 0 ? (
        <p className="text-slate-400 text-sm py-6 text-center">No targets set yet. Choose a focus skill to work towards.</p>
      ) : (
        <div className="space-y-2.5">
          {targets.map((t) => {
            const meta = SUBJECT_META_ALL[t.subject];
            const dom = subjects[t.subject]?.domains.find((d) => d.key === t.domain);
            const Icon = icons[t.status];
            const stStyle = TARGET_STATUS[t.status];
            return (
              <div
                key={t.id}
                data-testid={`target-${t.id}`}
                className="group flex items-start gap-3 p-3.5 rounded-xl border border-slate-100 hover:border-slate-200 transition"
              >
                <button
                  data-testid={`toggle-target-${t.id}`}
                  onClick={() => setStatus(t)}
                  className={`mt-0.5 h-7 w-7 rounded-full flex items-center justify-center shrink-0 transition ${stStyle.bg} ${stStyle.text}`}
                  title="Click to change status"
                >
                  <Icon size={16} />
                </button>
                <div className="flex-1 min-w-0">
                  <p className={`text-slate-800 font-medium ${t.status === "achieved" ? "line-through text-slate-400" : ""}`}>{t.text}</p>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: meta?.tint }}>{meta?.name}</span>
                    {dom && <span className="text-xs text-slate-400">· {dom.name}</span>}
                    {t.target_level && <span className="text-xs text-slate-400 inline-flex items-center gap-1">· Target <LevelChip level={t.target_level} /></span>}
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${stStyle.bg} ${stStyle.text}`}>{stStyle.name}</span>
                  </div>
                </div>
                <button
                  data-testid={`delete-target-${t.id}`}
                  onClick={() => remove(t.id)}
                  className="text-slate-300 hover:text-red-500 transition opacity-0 group-hover:opacity-100"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AddTargetModal({ studentId, subjects, onClose, onSaved }) {
  const subjKeys = Object.keys(subjects);
  const [subject, setSubject] = useState(subjKeys[0]);
  const [domain, setDomain] = useState(subjects[subjKeys[0]].domains[0].key);
  const [text, setText] = useState("");
  const [level, setLevel] = useState("");
  const [saving, setSaving] = useState(false);

  const domains = subjects[subject].domains;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/targets", {
        student_id: studentId, subject, domain, text,
        target_level: level ? Number(level) : null, priority: true,
      });
      toast.success("Target set");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Set a priority target" onClose={onClose}>
      <form onSubmit={save} className="space-y-4">
        <Field label="Subject">
          <select
            data-testid="target-subject-select"
            value={subject}
            onChange={(e) => { setSubject(e.target.value); setDomain(subjects[e.target.value].domains[0].key); }}
            className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 outline-none focus:border-[#81B29A]"
          >
            {subjKeys.map((k) => <option key={k} value={k}>{subjects[k].name}</option>)}
          </select>
        </Field>
        <Field label="Skill area">
          <select
            data-testid="target-domain-select"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 outline-none focus:border-[#81B29A]"
          >
            {domains.map((d) => <option key={d.key} value={d.key}>{d.name}</option>)}
          </select>
        </Field>
        <Field label="Target (next step)">
          <textarea
            data-testid="target-text-input"
            required
            rows={3}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="e.g. Blend 3-phoneme words independently without prompting"
            className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 outline-none focus:border-[#81B29A] resize-none"
          />
        </Field>
        <Field label="Target level (optional)">
          <select
            data-testid="target-level-select"
            value={level}
            onChange={(e) => setLevel(e.target.value)}
            className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 outline-none focus:border-[#81B29A]"
          >
            <option value="">No specific level</option>
            {domains.find((d) => d.key === domain)?.levels.map((l) => (
              <option key={l.level} value={l.level}>{`Level ${l.level} — ${l.descriptor.slice(0, 50)}`}</option>
            ))}
          </select>
        </Field>
        <button
          data-testid="save-target-btn"
          disabled={saving}
          className="w-full bg-[#4A5D6B] hover:bg-[#3A4A56] text-white rounded-full py-3 font-semibold font-['Outfit'] flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {saving && <Loader2 size={18} className="animate-spin" />} Save target
        </button>
      </form>
    </Modal>
  );
}


function AddIcon({ saving, added }) {
  if (saving) return <Loader2 size={13} className="animate-spin" />;
  if (added) return <Check size={13} />;
  return <Plus size={13} />;
}

function AiTargetsModal({ studentId, subjects, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    api.post(`/suggest/targets/${studentId}`)
      .then((r) => setItems((r.data.targets || []).map((t, i) => ({ ...t, _k: `${t.subject}-${t.domain}-${i}`, added: false, saving: false }))))
      .catch((e) => setError(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [studentId]);

  const add = async (idx) => {
    const t = items[idx];
    setItems((arr) => arr.map((x, i) => (i === idx ? { ...x, saving: true } : x)));
    try {
      await api.post("/targets", {
        student_id: studentId, subject: t.subject, domain: t.domain,
        text: t.text, target_level: t.target_level || null, priority: true,
      });
      setItems((arr) => arr.map((x, i) => (i === idx ? { ...x, added: true, saving: false } : x)));
      toast.success("Target added");
      onSaved();
    } catch (e) {
      setItems((arr) => arr.map((x, i) => (i === idx ? { ...x, saving: false } : x)));
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const addAll = async () => {
    for (let i = 0; i < items.length; i++) if (!items[i].added) await add(i);
  };

  return (
    <Modal title="AI-suggested priority targets" onClose={onClose}>
      <p className="text-sm text-slate-500 -mt-2 mb-4">
        Generated from this student's latest assessments. Review, then add the ones you want.
      </p>
      {loading && (
        <div className="flex flex-col items-center py-10 text-slate-400">
          <Loader2 className="animate-spin mb-2" />
          <span className="text-sm">Writing SMART targets…</span>
        </div>
      )}
      {!loading && error && (
        <p className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-3">{error}</p>
      )}
      {!loading && !error && (
        <>
          <div className="space-y-2.5 max-h-[55vh] overflow-y-auto pr-1">
            {items.map((t, idx) => {
              const meta = SUBJECT_META_ALL[t.subject];
              const dom = subjects[t.subject]?.domains.find((d) => d.key === t.domain);
              return (
                <div key={t._k || idx} data-testid={`ai-target-${idx}`} className="rounded-xl border border-slate-100 p-3.5">
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <textarea
                        rows={2}
                        value={t.text}
                        onChange={(e) => setItems((arr) => arr.map((x, i) => (i === idx ? { ...x, text: e.target.value } : x)))}
                        className="w-full text-sm text-slate-700 bg-[#F9F9F7] border border-slate-100 rounded-lg px-3 py-2 outline-none focus:border-[#81B29A] focus:bg-white resize-none"
                      />
                      <p className="text-[11px] mt-1.5 flex items-center gap-1.5">
                        <span className="font-bold uppercase tracking-wide" style={{ color: meta?.tint }}>{meta?.name}</span>
                        <span className="text-slate-400">· {dom?.name}{t.target_level ? ` · target level ${t.target_level}` : ""}</span>
                      </p>
                    </div>
                    <button
                      data-testid={`add-ai-target-${idx}`}
                      onClick={() => add(idx)}
                      disabled={t.added || t.saving}
                      className="shrink-0 inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold font-['Outfit'] transition disabled:opacity-70"
                      style={t.added ? { background: "#DCEBE4", color: "#3f7a5c" } : { background: "#4A5D6B", color: "#fff" }}
                    >
                      <AddIcon saving={t.saving} added={t.added} />
                      {t.added ? "Added" : "Add"}                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          {items.some((t) => !t.added) && (
            <button
              data-testid="add-all-ai-targets-btn"
              onClick={addAll}
              className="mt-4 w-full bg-[#4A5D6B] hover:bg-[#3A4A56] text-white rounded-full py-2.5 font-semibold font-['Outfit'] flex items-center justify-center gap-2"
            >
              <Plus size={16} /> Add all
            </button>
          )}
        </>
      )}
    </Modal>
  );
}
